import { type SceneDataQuery, type VizPanel } from '@grafana/scenes';

import { type HistogramType, type PanelConfig, type QueryConfig } from '../GmdVizPanel';
import { type PanelType } from './available-panel-types';
import { buildHeatmapPanel } from './heatmap/buildHeatmapPanel';
import { getHeatmapQueryRunnerParams } from './heatmap/getHeatmapQueryRunnerParams';
import { buildPercentilesPanel } from './percentiles/buildPercentilesPanel';
import { getPercentilesQueryRunnerParams } from './percentiles/getPercentilesQueryRunnerParams';
import { buildStatPanel } from './stat/buildStatPanel';
import { getStatQueryRunnerParams } from './stat/getStatQueryRunnerParams';
import { buildStatushistoryPanel } from './statushistory/buildStatushistoryPanel';
import { getStatushistoryQueryRunnerParams } from './statushistory/getStatushistoryQueryRunnerParams';
import { buildTimeseriesPanel } from './timeseries/buildTimeseriesPanel';
import { getTimeseriesQueryRunnerParams } from './timeseries/getTimeseriesQueryRunnerParams';

export type BuildVizPanelOptions = {
  metric: string;
  panelConfig: PanelConfig;
  queryConfig: QueryConfig;
  histogramType?: HistogramType; // heatmap & percentiles
};

export type GetQueryRunnerParamsOptions = {
  metric: string;
  queryConfig: QueryConfig;
  histogramType?: HistogramType; //  heatmap & percentiles
};

export type QueryRunnerParams = {
  maxDataPoints: number;
  queries: SceneDataQuery[];
  isRateQuery?: boolean;
};

export type Builders = {
  buildVizPanel: (options: BuildVizPanelOptions) => VizPanel;
  getQueryRunnerParams: (options: GetQueryRunnerParamsOptions) => QueryRunnerParams;
};

export const PANEL_TYPE_TO_BUILDERS_LOOKUP = new Map<PanelType, Builders>([
  [
    'timeseries',
    {
      buildVizPanel: buildTimeseriesPanel,
      getQueryRunnerParams: getTimeseriesQueryRunnerParams,
    },
  ],
  [
    'heatmap',
    {
      buildVizPanel: buildHeatmapPanel,
      getQueryRunnerParams: getHeatmapQueryRunnerParams,
    },
  ],
  [
    'percentiles',
    {
      buildVizPanel: buildPercentilesPanel,
      getQueryRunnerParams: getPercentilesQueryRunnerParams,
    },
  ],
  [
    'statushistory',
    {
      buildVizPanel: buildStatushistoryPanel,
      getQueryRunnerParams: getStatushistoryQueryRunnerParams,
    },
  ],
  [
    'stat',
    {
      buildVizPanel: buildStatPanel,
      getQueryRunnerParams: getStatQueryRunnerParams,
    },
  ],
]);

function getBuildersForPanelType(panelType: PanelType): Builders {
  const buildersForPanelType = PANEL_TYPE_TO_BUILDERS_LOOKUP.get(panelType);
  if (!buildersForPanelType) {
    throw new TypeError(`Unsupported panel type "${panelType}"!`);
  }
  return buildersForPanelType;
}

export const panelBuilder = {
  buildVizPanel(options: BuildVizPanelOptions) {
    return getBuildersForPanelType(options.panelConfig.type).buildVizPanel(options);
  },
  getQueryRunnerParams(options: GetQueryRunnerParamsOptions & { panelType: PanelType }) {
    const { metric, queryConfig, panelType } = options;
    return getBuildersForPanelType(panelType).getQueryRunnerParams({ metric, queryConfig });
  },
};
